import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import OrderTransferFilters from "./OrderTransferFilters";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useForm } from "react-hook-form";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useNavigate } from "react-router";
import { useRouter } from "@/app/hooks/useRouter";
import { useOrderTransferListAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useOrderTransferListAPI";
import { IClinicToClinicTransferOrdersFilterBody } from "@/api_framework/api_modals/Inventory";
import { useEffect, useState } from "react";
import { fromUnixTime, format } from "date-fns";
import { toNumber } from "lodash";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { useTONumberAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useTONumberAPI";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setTransferInventoryItems } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useTransferOrderDetailsAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useTransferOrderDetailsAPI";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useLoader } from "@/app/hooks/useLoader";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import RBACFlag from "@/app/components/core/RBACFlag";
import { FaRegCopy } from "react-icons/fa6";
import { toast } from "react-hot-toast";
import TOCancelModal from "./TOCancelModal";
import { useUtils } from "@/app/hooks/useUtils";
import { PRIMARY_BUTTON } from "@/constants";
import clsx from "clsx";

export interface IOrderTransferProps {
  inventoryTypes: string;
}

const inventoryTypesList = [
  {
    id: "1",
    type: "Transfer Good Inventory",
  },
  {
    id: "2",
    type: "Transfer Bad Inventory",
  },
];

const ClinicToClinicTransfer = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const inventoryOptions = useSelectMenuReducer(inventoryTypesList, "type", "id");
  const [orderFilters, setOrderFilters] = useState<IClinicToClinicTransferOrdersFilterBody>();
  const { execute: orderTransferList, data } = useOrderTransferListAPI();
  const getTONumber = useTONumberAPI();
  const [inputSearchWithTo, setInputSearchWithTo] = useState<string>("");
  const dispatch = useAppDispatch();
  const transferOrderDetails = useTransferOrderDetailsAPI();
  const { NO_APPOINTMENTS } = useImageConstants();
  const [_loading, setLoading] = useState(true);
  const isProductMoveGoodToBadInProgess = useLoader("isInventoryProductMoveGoodToBadInProgress");
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [selectedTOId, setSelectedTOId] = useState<string | null>(null);

  const { getStatusColor } = useUtils();

  const formHook = useForm<IOrderTransferProps>({
    mode: "onChange",
    defaultValues: {},
  });

  dispatch(setTransferInventoryItems({ data: null }));

  const handeFetchTransferOrderList = () => {
    const filterBody = {
      ...orderFilters,
    } as IClinicToClinicTransferOrdersFilterBody;
    setLoading(true);
    orderTransferList(filterBody).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (orderFilters) {
      handeFetchTransferOrderList();
    }
  }, [orderFilters]);

  const handleSearchTransferOrders = () => {
    if (inputSearchWithTo?.length < 1) return;
    orderTransferList({ transfer_order_number: inputSearchWithTo });
  };

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      handleSearchTransferOrders();
    }
  };

  const handleTOView = (id: string) => {
    transferOrderDetails(id);
    window.open(`${getRouteKey("TRANSFER_ORDER_DETAILS", "url")}/${id}`, "_blank");
  };

  const handleCancelClick = (id: string) => {
    setSelectedTOId(id);
    setOpenCancelModal(true);
  };

  return (
    <>
      <div className="m-2 sm:m-6 rounded-md bg-white px-2 py-1">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-2">
          <h2 className="justify-start text-black text-base font-semibold tracking-wide">Transfer Orders To Other Clinics</h2>
          <RBACFlag actionId="TRANSFER_ORDERS_ACTION">
            <div className="w-full sm:w-64">
              <SearchableSelectMenu
                errorMessages={[]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("inventoryTypes", item.id);
                    getTONumber();
                    navigate(
                      getRouteKey(item.id === "1" ? "TRANSFER_GOOD_INVENTORY" : "TRANSFER_BAD_INVENTORY", "url"),
                    );
                  }
                }}
                fieldError={formHook?.formState?.errors?.inventoryTypes}
                register={formHook.register("inventoryTypes")}
                selectItems={inventoryOptions}
                placeholder="Transfer Inventory"
                showTooltips={false}
                showTypedErrors={false}
                showDropdownIcon={true}
                listBoxClassName="w-full"
                className="text-gray-800 cursor-pointer"
                containerClassName="w-full"
              />
            </div>
          </RBACFlag>
        </div>
        <OrderTransferFilters setOrderFilters={setOrderFilters} />
      </div>
      <div className="m-2 sm:m-6 rounded-md bg-white pt-1 px-2">
        <div className="sm:flex-auto">
          <InputWithStartAndEndAddOn
            value={inputSearchWithTo}
            containerClassName="w-full sm:w-1/3"
            placeholder="Search by TO ID"
            onChange={(e) => setInputSearchWithTo(e.target.value)}
            onKeyDown={handleEnterPress}
            endAddon={
              <button onClick={handleSearchTransferOrders} className="font-semibold text-xs cursor-pointer">
                <MagnifyingGlassIcon className="w-4 h-4 hover:text-blue-500 hover:scale-105 transition-transform" />
              </button>
            }
            startAddon={<span className="font-semibold text-xs cursor-pointer">TO #</span>}
          />
        </div>
        {isProductMoveGoodToBadInProgess ? (
          <TableSkeleton />
        ) : data?.length > 0 ? (
          <div className="mt-8 outer-container relative rounded-xl bg-[#F9FAFB]">
            <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px]">
              <div className="bg-white rounded-lg">
                <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                    <tr>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[150px]">
                        TO ID
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[110px]">
                        Creation Date
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[170px]">
                        Sender Clinic
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[170px]">
                        Receiver Clinic
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[140px]">
                        Inventory Type
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[140px]">
                        Created By
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[160px]">
                        Rider Details
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[150px]">
                        TO Status
                      </th>
                      <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[110px]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="overflow-hidden rounded-xl w-full">
                    {data?.map((item, index) => {
                      const isFirst = index === 0;
                      const isLast = index === data.length - 1;
                      const isSingle = data.length === 1;

                      return (
                        <tr
                          key={index}
                          className={clsx(
                            "bg-white transition",
                            isSingle && "rounded-xl",
                            isFirst && !isSingle && "rounded-t-xl",
                            isLast && !isSingle && "rounded-b-xl",
                          )}
                        >
                          <td
                            className={clsx(
                              "px-3 py-3 whitespace-nowrap text-xs font-medium",
                              isSingle && "rounded-tl-2xl rounded-bl-2xl",
                              isFirst && !isSingle && "rounded-tl-2xl",
                              isLast && !isSingle && "rounded-bl-2xl",
                            )}
                          >
                            <div className="flex gap-3 items-center justify-start">
                              <div
                                className="text-blue-700 cursor-pointer hover:underline"
                                onClick={() => {
                                  handleTOView(item?.transfer_order_id);
                                }}
                              >
                                {item?.transfer_order_number}
                              </div>
                              <div className="relative group cursor-pointer">
                                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                  Copy
                                </div>
                                <FaRegCopy
                                  className="text-gray-500 hover:text-blue-500"
                                  onClick={() => {
                                    navigator.clipboard.writeText(item?.transfer_order_number || "");
                                    toast.success(`${item?.transfer_order_number} copied`);
                                  }}
                                />
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                            {format(fromUnixTime(toNumber(item?.created_at)), "dd/MM/yyyy")}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                            {item?.transfer_order_clinic_details?.sender_clinic_name}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                            {item?.transfer_order_clinic_details?.receiver_clinic_name}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                            {item?.inventory_type}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                            {item?.created_by}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                            <div>
                              <p>{item?.rider_details?.rider_name}</p>
                              <p className="mt-2 whitespace-nowrap">Drop OTP: {item?.rider_details?.drop_otp} </p>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                            <div
                              style={getStatusColor(item?.transfer_order_status)}
                              className="flex justify-start items-center px-2 py-1.5 rounded-[20px] text-xs w-auto text-left"
                            >
                              {item?.transfer_order_status}
                            </div>
                          </td>
                          <td
                            className={clsx(
                              "px-3 py-3 whitespace-nowrap text-xs font-normal",
                              isSingle && "rounded-tr-2xl rounded-br-2xl",
                              isFirst && !isSingle && "rounded-tr-2xl",
                              isLast && !isSingle && "rounded-br-2xl",
                            )}
                          >
                            {item?.show_cancel_button && (
                              <button
                                className={`${PRIMARY_BUTTON} !h-8 !px-4 !py-2 !text-[12px]`}
                                onClick={() => handleCancelClick(item?.transfer_order_id)}
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full bg-white rounded-md flex justify-center">
            <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Data Found." />
          </div>
        )}
      </div>
      <TOCancelModal
        open={openCancelModal}
        setOpen={setOpenCancelModal}
        selectedTOId={selectedTOId}
        handeFetchTransferOrderList={() => handeFetchTransferOrderList()}
      />
    </>
  );
};

export default ClinicToClinicTransfer;
